import React, { useState, useEffect, useRef } from "react";
import {
  TimeSlot,
  BookingData,
  Service,
  Professional,
  ServiceType,
} from "../../../types";
import {
  formatDateForDisplay,
  applyBirthdayMask,
  validateBirthday,
  convertBirthdayToAPIFormat,
} from "../../../utils/dateUtils";
import {
  handlePhoneInputChange,
  cleanPhoneNumber,
  validatePhoneNumber,
} from "../../../utils/phoneUtils";
import { useTheme } from "../../../contexts/ThemeContext";
import { ActionButton } from "../../shared";
import { getServiceIcon } from "../../../utils/themeUtils";

interface BookingFormProps {
  selectedDate: string;
  selectedTimeSlot?: TimeSlot | null;
  selectedService: Service;
  selectedSubServices?: number[];
  selectedProfessional?: Professional | null;
  phoneNumber?: string;
  onSubmit: (bookingData: BookingData) => Promise<void>;
  clientNotFound?: boolean;
  loading: boolean;
}

export const BookingForm: React.FC<BookingFormProps> = ({
  selectedDate,
  selectedTimeSlot,
  selectedService,
  selectedSubServices = [],
  selectedProfessional,
  phoneNumber: initialPhoneNumber,
  onSubmit,
  clientNotFound = false,
  loading,
}) => {
  const { company } = useTheme();
  const [phoneNumber, setPhoneNumber] = useState(() => {
    // Check local storage first, then use initialPhoneNumber, then empty string
    const storedPhone = localStorage.getItem("bookingPhoneNumber");
    return storedPhone || initialPhoneNumber || "";
  });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [showNameInput, setShowNameInput] = useState(false);
  const [clientName, setClientName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [birthday, setBirthday] = useState("");
  const [birthdayError, setBirthdayError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Show name input when client is not found
  useEffect(() => {
    if (clientNotFound && !showNameInput) {
      setShowNameInput(true);
    }
  }, [clientNotFound, showNameInput]);

  // Focus on name input when it becomes visible
  useEffect(() => {
    if (showNameInput && nameInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [showNameInput]);

  // Sky color palette for consistency
  const primaryColor = "#0ea5e9"; // sky-500
  const primaryBg = "#0ea5e9"; // sky-500
  const primaryLight = "#e0f2fe"; // sky-100
  const primaryLighter = "#f0f9ff"; // sky-50

  // Calculate total price including selected sub-services
  const calculateTotalPrice = () => {
    let total = parseFloat(selectedService.price) || 0;

    if (selectedSubServices && selectedSubServices.length > 0) {
      selectedSubServices.forEach((subServiceId) => {
        const relation = selectedService.subServiceRelations?.find(
          (rel) => rel.subService.id === subServiceId
        );
        if (relation) {
          total += parseFloat(relation.price) || 0;
        }
      });
    }

    return total;
  };

  const totalPrice = calculateTotalPrice();

  // Get selected sub-service details for display
  const getSelectedSubServices = () => {
    if (!selectedSubServices || selectedSubServices.length === 0) return [];

    return selectedSubServices
      .map((subServiceId) => {
        const relation = selectedService.subServiceRelations?.find(
          (rel) => rel.subService.id === subServiceId
        );
        return relation;
      })
      .filter(Boolean);
  };

  const selectedSubServiceDetails = getSelectedSubServices();

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handlePhoneInputChange(value, setPhoneNumber);

    // Save to local storage whenever phone number changes
    if (value.trim()) {
      localStorage.setItem("bookingPhoneNumber", value);
    }

    if (phoneError) {
      setPhoneError(null);
    }
  };

  const handleSubmit = async () => {
    const cleanPhone = cleanPhoneNumber(phoneNumber);
    const phoneValidation = validatePhoneNumber(phoneNumber);

    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error || "Telefone inválido");
      return;
    }

    // If we're showing the name input, validate the name and birthday
    if (showNameInput) {
      if (!clientName.trim()) {
        setNameError("Nome é obrigatório");
        return;
      }
      if (clientName.trim().length < 2) {
        setNameError("Nome deve ter pelo menos 2 caracteres");
        return;
      }
      const birthdayValidation = validateBirthday(birthday);
      if (!birthdayValidation.isValid) {
        setBirthdayError(birthdayValidation.error || "Data de nascimento inválida");
        return;
      }
    }

    // Save the valid phone number to local storage
    localStorage.setItem("bookingPhoneNumber", phoneNumber);

    const bookingData: BookingData = {
      date: selectedDate,
      phoneNumber: cleanPhone,
      clientName: clientName.trim() || undefined,
      birthday: showNameInput ? convertBirthdayToAPIFormat(birthday.trim()) : undefined,
      serviceId: selectedService.id,
      serviceType: selectedService.type,
      professionalId: selectedProfessional?.id,
      startTime:
        selectedService.type === ServiceType.TIME
          ? selectedTimeSlot?.startTime || "09:00"
          : undefined,
      subServiceIds:
        selectedSubServices.length > 0 ? selectedSubServices : undefined,
    };

    await onSubmit(bookingData);
  };

  return (
    <div className="space-y-8 animate-slide-up">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(to right, ${primaryBg}, ${primaryColor})`,
            }}
          >
            <span className="text-white text-base sm:text-lg font-bold">
              {selectedService.type === ServiceType.TIME ? "4" : "3"}
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
            Confirmar Agendamento
          </h3>
        </div>
        <p className="text-sm sm:text-base text-gray-600 px-4">
          Revise os detalhes e confirme seu agendamento
        </p>
      </div>

      <div className="max-w-lg mx-auto">
        <div
          className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 shadow-soft overflow-hidden"
          style={{ borderColor: "#bae6fd" }} // sky-200
        >
          <div
            className="px-4 sm:px-6 py-3 sm:py-4"
            style={{
              background: `linear-gradient(to right, ${primaryBg}, ${primaryColor})`,
            }}
          >
            <h4 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <span className="text-lg sm:text-xl">📋</span>
              Detalhes do Agendamento
            </h4>
          </div>

          <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: primaryLight }}
                  >
                    <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                      📅
                    </span>
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-700">Data:</span>
                </div>
                <span className="font-semibold text-sm sm:text-base text-gray-800 text-right ml-2">
                  {formatDateForDisplay(selectedDate)}
                </span>
              </div>

              {selectedService &&
                selectedTimeSlot &&
                selectedService.type === ServiceType.TIME && (
                  <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 text-xs sm:text-sm">⏰</span>
                      </div>
                      <span className="font-medium text-sm sm:text-base text-gray-700">
                        Horário:
                      </span>
                    </div>
                    <span className="font-semibold text-sm sm:text-base text-gray-800 text-right ml-2">
                      {selectedTimeSlot.startTime}
                    </span>
                  </div>
                )}

              <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: primaryLight }}
                  >
                    <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                      {getServiceIcon(company)}
                    </span>
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-700">Serviço:</span>
                </div>
                <span className="font-semibold text-sm sm:text-base text-gray-800 text-right ml-2 truncate">
                  {selectedService.name}
                </span>
              </div>

              {selectedProfessional && (
                <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: primaryLight }}
                    >
                      <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                        👨‍💼
                      </span>
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-700">
                      Profissional:
                    </span>
                  </div>
                  <span className="font-semibold text-sm sm:text-base text-gray-800 text-right ml-2 truncate">
                    {selectedProfessional.name}
                  </span>
                </div>
              )}

              {selectedService && selectedService.type === ServiceType.TIME && (
                <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: primaryLight }}
                    >
                      <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                        ⏰
                      </span>
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-700">Duração:</span>
                  </div>
                  <span className="font-semibold text-sm sm:text-base text-gray-800 text-right ml-2">
                    {selectedService.duration} minutos
                  </span>
                </div>
              )}

              {/* Main Service Price */}
              <div className="flex items-center justify-between py-2 sm:py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-600 text-xs sm:text-sm">
                      {" "}
                      {getServiceIcon(company)}
                    </span>
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-700 truncate">
                    {selectedService.name}:
                  </span>
                </div>
                <span className="font-semibold text-sm sm:text-base text-gray-800 text-right ml-2 whitespace-nowrap">
                  R$ {(parseFloat(selectedService.price) || 0).toFixed(2)}
                </span>
              </div>

              {/* Selected Sub-Services */}
              {selectedSubServiceDetails.map((relation, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 pl-4 sm:pl-6 border-b border-gray-50"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-600 text-xs">+</span>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-600 truncate">
                      {relation?.subService.name}:
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-800 text-right ml-2 whitespace-nowrap">
                    R$ {(parseFloat(relation!.price) || 0).toFixed(2)}
                  </span>
                </div>
              ))}

              {/* Total Price */}
              <div
                className="flex items-center justify-between py-2 sm:py-3 rounded-lg px-3 sm:px-4 mt-2"
                style={{ backgroundColor: primaryLighter }}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: primaryLight }}
                  >
                    <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                      💰
                    </span>
                  </div>
                  <span className="font-bold text-sm sm:text-base text-gray-700">Total:</span>
                </div>
                <span
                  className="font-bold text-lg sm:text-xl"
                  style={{ color: primaryColor }}
                >
                  R$ {totalPrice.toFixed(2)}
                </span>
              </div>

              <div className="py-2 sm:py-3">
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: primaryLight }}
                  >
                    <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                      📱
                    </span>
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-700">Telefone:</span>
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  className={`w-full px-4 py-3 border rounded-lg text-gray-800 font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 ${
                    phoneError
                      ? "border-gray-500 focus:border-gray-500 focus:ring-gray-200"
                      : "border-gray-300 focus:ring-2"
                  }`}
                  style={
                    !phoneError
                      ? {
                          borderColor: phoneError ? undefined : "#0ea5e9", // sky-500
                        }
                      : {}
                  }
                  onFocus={(e) => {
                    if (!phoneError) {
                      e.target.style.borderColor = primaryColor;
                      e.target.style.boxShadow = `0 0 0 3px #bae6fd`; // sky-200
                    }
                  }}
                  onBlur={(e) => {
                    if (!phoneError) {
                      e.target.style.borderColor = "#d1d5db";
                      e.target.style.boxShadow = "none";
                    }
                  }}
                />
                {phoneError && (
                  <p className="mt-2 text-sm text-gray-600">{phoneError}</p>
                )}
              </div>

              {/* Name Input - Only shown when client doesn't exist */}
              {showNameInput && (
                <div className="py-2 sm:py-3 animate-slide-up">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: primaryLight }}
                    >
                      <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                        👤
                      </span>
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-700">
                      Nome:
                    </span>
                  </div>
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={clientName}
                    onChange={(e) => {
                      setClientName(e.target.value);
                      if (nameError) setNameError(null);
                    }}
                    placeholder="Digite seu nome"
                    className={`w-full px-4 py-3 border rounded-lg text-gray-800 font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 ${
                      nameError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-300 focus:ring-2"
                    }`}
                    style={
                      !nameError
                        ? {
                            borderColor: "#0ea5e9", // sky-500
                          }
                        : {}
                    }
                    onFocus={(e) => {
                      if (!nameError) {
                        e.target.style.borderColor = primaryColor;
                        e.target.style.boxShadow = `0 0 0 3px #bae6fd`; // sky-200
                      }
                    }}
                    onBlur={(e) => {
                      if (!nameError) {
                        e.target.style.borderColor = "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }
                    }}
                  />
                  {nameError && (
                    <p className="mt-2 text-sm text-red-600">{nameError}</p>
                  )}
                  
                  {/* Birthday Input */}
                  <div className="mt-4">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: primaryLight }}
                      >
                        <span style={{ color: primaryColor }} className="text-xs sm:text-sm">
                          🎂
                        </span>
                      </div>
                    <span className="font-medium text-sm sm:text-base text-gray-700">
                      Data de Nascimento:
                    </span>
                    </div>
                    <input
                      type="text"
                      value={birthday}
                      onChange={(e) => {
                        const maskedValue = applyBirthdayMask(e.target.value);
                        setBirthday(maskedValue);
                        if (birthdayError) setBirthdayError(null);
                      }}
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      required
                      className={`w-full px-4 py-3 border rounded-lg text-gray-800 font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        birthdayError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-300 focus:ring-2"
                      }`}
                      style={
                        !birthdayError
                          ? {
                              borderColor: "#0ea5e9", // sky-500
                            }
                          : {}
                      }
                      onFocus={(e) => {
                        if (!birthdayError) {
                          e.target.style.borderColor = primaryColor;
                          e.target.style.boxShadow = `0 0 0 3px #bae6fd`; // sky-200
                        }
                      }}
                      onBlur={(e) => {
                        // Validate on blur
                        const validation = validateBirthday(e.target.value);
                        if (!validation.isValid && e.target.value.trim()) {
                          setBirthdayError(validation.error || "Data inválida");
                        } else if (validation.isValid) {
                          setBirthdayError(null);
                        }
                        // Apply blur styling
                        const hasError = !validation.isValid && e.target.value.trim();
                        if (!hasError) {
                          e.target.style.borderColor = "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }
                      }}
                    />
                    {birthdayError && (
                      <p className="mt-2 text-sm text-red-600">{birthdayError}</p>
                    )}
                  </div>
                  
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">📝 Novo cliente:</span> Como
                      este é seu primeiro agendamento, precisamos do seu nome
                      para criar seu cadastro.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <ActionButton
          onClick={handleSubmit}
          variant="primary"
          loading={loading}
          className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-2xl font-bold text-base sm:text-lg justify-center mt-4 sm:mt-6"
        >
          {!loading && (
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <span className="text-lg sm:text-xl">✨</span>
              <span className="text-sm sm:text-base">Confirmar Agendamento</span>
              <span className="text-lg sm:text-xl">✨</span>
            </div>
          )}
          {loading && <span className="text-sm sm:text-base">Agendando...</span>}
        </ActionButton>
      </div>
    </div>
  );
};
